import React, { useState, useEffect, useRef } from "react";
import { TextField, InputAdornment, IconButton, Box } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

/**
 * Date field shown as yyyy/mm/dd.
 *
 * The native <input type="date"> renders in the browser's locale order
 * (mm/dd/yyyy here) and there is no prop/CSS to force yyyy/mm/dd. So this is a
 * masked text field (type-to-enter, auto-inserts slashes) plus a calendar icon
 * that opens the native picker. It stores/emits an ISO `yyyy-mm-dd` string via
 * `onChange`, or "" while the entry is incomplete — so callers keep passing the
 * same ISO value the backend expects.
 *
 * Use this instead of a raw `TextField type="date"` anywhere a date is entered.
 *
 * Props:
 *   value      ISO yyyy-mm-dd string (or "")
 *   onChange   (isoString | "") => void
 *   sx         forwarded to the TextField
 *   iconColor  calendar icon colour (default matches the muted token)
 */
export default function DateFieldYMD({
  value,
  onChange,
  sx,
  iconColor = "#64748B",
  placeholder = "yyyy/mm/dd",
  size = "small",
  fullWidth = true,
  ...rest
}) {
  const [text, setText] = useState(value ? value.replace(/-/g, "/") : "");
  const nativeRef = useRef(null);

  // reflect a committed value (e.g. chosen from the picker) as yyyy/mm/dd.
  // guarded on a truthy value so a half-typed entry (ISO "" until complete)
  // isn't wiped mid-typing.
  useEffect(() => {
    if (value) setText(value.replace(/-/g, "/"));
  }, [value]);

  const handleText = (e) => {
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 8);
    let out = digits.slice(0, 4);
    if (digits.length > 4) out += "/" + digits.slice(4, 6);
    if (digits.length > 6) out += "/" + digits.slice(6, 8);
    setText(out);
    onChange(digits.length === 8 ? `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}` : "");
  };

  const openPicker = () => {
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try { el.showPicker(); return; } catch (_) { /* fall through to focus */ }
    }
    el.focus();
    el.click();
  };

  return (
    <TextField
      type="text"
      value={text}
      onChange={handleText}
      placeholder={placeholder}
      size={size}
      fullWidth={fullWidth}
      sx={sx}
      inputProps={{ dir: "ltr", inputMode: "numeric", maxLength: 10, style: { textAlign: "left" } }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ position: "relative" }}>
            <IconButton size="small" edge="end" onClick={openPicker} sx={{ color: iconColor }}>
              <CalendarMonthIcon fontSize="small" />
            </IconButton>
            <Box
              component="input"
              ref={nativeRef}
              type="date"
              value={value || ""}
              onChange={(e) => onChange(e.target.value || "")}
              tabIndex={-1}
              aria-hidden="true"
              sx={{ position: "absolute", right: 0, bottom: 0, width: "1px", height: "1px", opacity: 0, border: 0, p: 0, pointerEvents: "none" }}
            />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
}
